/**
 * Postura a partir de uma foto, medida dentro do aparelho.
 *
 * O modelo (MoveNet SinglePose Lightning, 9 MB) devolve 17 pontos do corpo. O
 * que interessa numa foto de perfil sentado são dois números:
 *
 *   - **projeção da cabeça**: o quanto a orelha está à frente do ombro;
 *   - **inclinação do tronco**: o ângulo da reta ombro→quadril contra a vertical.
 *
 * Os dois saem SEM ESCALA — a projeção em frações do próprio tronco, não em
 * centímetros. É uma decisão, não uma limitação técnica que ficou por fazer:
 * uma foto sem referência de tamanho não permite converter pixel em
 * centímetro, e "sua cabeça está 7 cm à frente" seria um chute com cara de
 * medida. Fração de tronco a pessoa consegue conferir olhando.
 *
 * Nada disso vai para lugar nenhum: os pixels são lidos do anexo que já está no
 * aparelho, o modelo roda no processo do app, e a resposta vira rascunho.
 */

const PONTOS = [
  "nariz", "olho_e", "olho_d", "orelha_e", "orelha_d",
  "ombro_e", "ombro_d", "cotovelo_e", "cotovelo_d", "pulso_e", "pulso_d",
  "quadril_e", "quadril_d", "joelho_e", "joelho_d", "tornozelo_e", "tornozelo_d",
] as const;

/** Abaixo disto o ponto é chute do modelo. Vale para os três que usamos. */
const CONFIANCA_MINIMA = 0.3;

export const MODELO_POSE = {
  nome: "Postura (MoveNet Lightning)",
  arquivo: "movenet-singlepose-lightning.onnx",
  url: "https://huggingface.co/Xenova/movenet-singlepose-lightning/resolve/main/onnx/model.onnx",
  mb: 9,
  descricao:
    "Marca ombro, orelha e quadril numa foto e mede a postura. São 9 MB e roda " +
    "em menos de um segundo — a foto não sai do aparelho.",
};

export interface Ponto {
  x: number;
  y: number;
  conf: number;
}

export type Corpo = Record<string, Ponto>;

interface PonteVisaoPose {
  pontosDoCorpo(dataUrl: string): string;
  carregarVisao?(nomeOuCaminho: string): string;
  estadoVisao?(): string;
}

function ponte(): PonteVisaoPose | null {
  const p = typeof window !== "undefined"
    ? (window as unknown as { wuweiAndroidLlm?: Partial<PonteVisaoPose> }).wuweiAndroidLlm
    : undefined;
  return p && typeof p.pontosDoCorpo === "function" ? (p as PonteVisaoPose) : null;
}

/** Já tentou carregar nesta sessão? Evita repetir a espera a cada foto. */
let tentouCarregar = false;

/**
 * Garante o modelo carregado, carregando-o se o arquivo estiver no aparelho.
 *
 * Existe por um defeito visto no aparelho, não imaginado: o motor solta o
 * modelo quando o app é encerrado, e ao reabrir a análise respondia "o modelo
 * não está carregado" para quem já o tinha baixado. A pessoa não distingue
 * "não baixei" de "o app reiniciou" — e a segunda não deveria ser problema
 * dela. São 9 MB e menos de um segundo de carga.
 */
function garantirCarregado(p: PonteVisaoPose): void {
  if (tentouCarregar || typeof p.carregarVisao !== "function") return;
  tentouCarregar = true;
  try {
    p.carregarVisao(MODELO_POSE.arquivo);
  } catch {
    // Sem modelo no aparelho, `pontosDoCorpo` devolve vazio e o texto de
    // "baixe o modelo" continua sendo a resposta certa.
  }
}

export function disponivel(): boolean {
  return ponte() !== null;
}

/** Põe a imagem num quadrado com bordas e devolve o dataURL e as medidas.
 *
 *  Bordas, e não esticar: distorcer a foto para caber no quadrado do modelo
 *  mudaria todos os ângulos — e ângulo é exatamente o que se vai medir. O
 *  deslocamento fica registrado para converter os pontos de volta. */
export async function prepararQuadrado(
  blob: Blob,
): Promise<{ dataUrl: string; lado: number; dx: number; dy: number; largura: number; altura: number }> {
  const bitmap = await createImageBitmap(blob);
  const lado = Math.max(bitmap.width, bitmap.height);
  const dx = (lado - bitmap.width) / 2;
  const dy = (lado - bitmap.height) / 2;
  const tela = document.createElement("canvas");
  tela.width = lado;
  tela.height = lado;
  const ctx = tela.getContext("2d");
  if (!ctx) throw new Error("não deu para preparar a imagem");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, lado, lado);
  ctx.drawImage(bitmap, dx, dy);
  bitmap.close?.();
  return {
    dataUrl: tela.toDataURL("image/jpeg", 0.92),
    lado,
    dx,
    dy,
    largura: bitmap.width,
    altura: bitmap.height,
  };
}

/** Converte a saída crua (y, x, confiança em 0..1) em pontos na foto original. */
export function lerPontos(
  cru: number[],
  quadro: { lado: number; dx: number; dy: number },
): Corpo {
  const corpo: Corpo = {};
  for (let i = 0; i < PONTOS.length; i++) {
    const y = cru[i * 3];
    const x = cru[i * 3 + 1];
    const conf = cru[i * 3 + 2];
    if (y === undefined || x === undefined) continue;
    corpo[PONTOS[i]] = {
      x: x * quadro.lado - quadro.dx,
      y: y * quadro.lado - quadro.dy,
      conf,
    };
  }
  return corpo;
}

export interface Medidas {
  lado: "e" | "d";
  projecaoEmTroncos: number;
  inclinacaoGraus: number;
  confianca: number;
}

/** Qual lado do corpo o modelo enxergou melhor.
 *
 *  Foto de perfil mostra um lado só, e o outro vem com confiança baixa e
 *  posição inventada. Medir pelo lado errado dá um número plausível e falso. */
function ladoVisivel(c: Corpo): { lado: "e" | "d"; conf: number } {
  const min = (l: string) =>
    Math.min(c[`orelha_${l}`]?.conf ?? 0, c[`ombro_${l}`]?.conf ?? 0, c[`quadril_${l}`]?.conf ?? 0);
  const e = min("e");
  const d = min("d");
  return e >= d ? { lado: "e", conf: e } : { lado: "d", conf: d };
}

export function medir(c: Corpo): Medidas | null {
  const { lado, conf } = ladoVisivel(c);
  const orelha = c[`orelha_${lado}`];
  const ombro = c[`ombro_${lado}`];
  const quadril = c[`quadril_${lado}`];
  if (!orelha || !ombro || !quadril) return null;
  if (Math.min(orelha.conf, ombro.conf, quadril.conf) < CONFIANCA_MINIMA) return null;

  const tronco = Math.hypot(ombro.x - quadril.x, ombro.y - quadril.y);
  if (tronco < 1) return null;

  const projecao = Math.abs(orelha.x - ombro.x) / tronco;
  const inclinacao =
    (Math.atan2(Math.abs(ombro.x - quadril.x), Math.abs(ombro.y - quadril.y)) * 180) / Math.PI;
  return {
    lado,
    projecaoEmTroncos: Number(projecao.toFixed(3)),
    inclinacaoGraus: Number(inclinacao.toFixed(1)),
    confianca: Number(conf.toFixed(2)),
  };
}

/** O texto que a pessoa lê. Descritivo primeiro, sugestão depois.
 *
 *  Os cortes (0,25 e 0,45 de tronco; 10 e 20 graus) são faixas de leitura, não
 *  diagnóstico — e o texto diz isso. Postura tem literatura clínica, este app
 *  tem uma foto: prometer avaliação seria passar de ferramenta a laudo. */
export function emPortugues(m: Medidas | null): string {
  if (!m) {
    return (
      "Não deu para medir: preciso ver orelha, ombro e quadril do mesmo lado. " +
      "Uma foto de perfil, com o corpo inteiro do tronco para cima, costuma resolver."
    );
  }
  const p = m.projecaoEmTroncos;
  const cabeca =
    p < 0.25
      ? "Cabeça alinhada com o ombro."
      : p < 0.45
        ? `Cabeça um pouco à frente do ombro (${p} do tronco).`
        : `Cabeça bem à frente do ombro (${p} do tronco) — é o padrão de quem passa horas olhando para baixo.`;
  const tronco =
    m.inclinacaoGraus < 10
      ? "Tronco na vertical."
      : m.inclinacaoGraus < 20
        ? `Tronco inclinado ${m.inclinacaoGraus}°.`
        : `Tronco bastante inclinado (${m.inclinacaoGraus}°).`;
  const dica =
    p >= 0.45
      ? " Subir a tela até a altura dos olhos costuma corrigir a maior parte disso."
      : p >= 0.25
        ? " Vale conferir a altura da tela."
        : "";
  return `${cabeca} ${tronco}${dica}\n\nMedido no aparelho, pela foto. Não é avaliação clínica.`;
}

/** Faz tudo: prepara, roda o modelo e devolve o texto e os pontos. */
export async function analisar(blob: Blob): Promise<{ texto: string; corpo: Corpo | null }> {
  const p = ponte();
  if (!p) {
    return {
      texto:
        "A análise de postura roda em código nativo, dentro do app instalado. " +
        "Neste navegador não existe essa camada.",
      corpo: null,
    };
  }
  const quadro = await prepararQuadrado(blob);
  let cru: number[] = [];
  try {
    cru = JSON.parse(p.pontosDoCorpo(quadro.dataUrl)) as number[];
    if (!cru.length) {
      // Vazio pode ser "não tem modelo" ou "o motor soltou o modelo quando o
      // app fechou". A segunda tem conserto; tenta e roda de novo.
      garantirCarregado(p);
      cru = JSON.parse(p.pontosDoCorpo(quadro.dataUrl)) as number[];
    }
  } catch {
    cru = [];
  }
  if (!cru.length) {
    return {
      texto:
        "O modelo de postura não está carregado. Baixe “Postura (MoveNet)” na tela " +
        "de agentes locais — são 9 MB.",
      corpo: null,
    };
  }
  const corpo = lerPontos(cru, quadro);
  return { texto: emPortugues(medir(corpo)), corpo };
}

declare global {
  interface Window {
    __wuweiPostura: { lerPontos: typeof lerPontos; medir: typeof medir; emPortugues: typeof emPortugues };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiPostura = { lerPontos, medir, emPortugues };
}
